import React, { useEffect } from "react"

import { getLogoFontsStylesheetUrl } from "../constants/logoFonts"

/**
 * Renders the text logo (main line + optional second line) for header or footer.
 * Used by HeaderSectionDesktop/Mobile and FooterSectionDesktop/Mobile when theme uses text logo.
 * Supports Figma HM Pro Experience logo fields: main/second font size, letter spacing, line spacing, alignment.
 */
type TextLogoViewProps = {
  mainLine: string
  secondLine?: string
  mainFont: string
  secondFont: string
  color: string
  /** Base font size for main line (e.g. "14px" for header, "18px" for footer). */
  mainFontSize?: string
  /** Scale for second line when secondFontSize not provided (e.g. 0.5 = half size). */
  secondLineScale?: number
  /** Explicit second line font size (e.g. "10px"). When set, overrides secondLineScale. */
  secondFontSize?: string
  /** Main line letter-spacing in hundredths of em (e.g. 10 = 0.1em). */
  mainLetterSpacing?: number
  /** Second line letter-spacing in hundredths of em (e.g. 35 = 0.35em). */
  secondLetterSpacing?: number
  /** Gap between main and second line in px. */
  lineSpacing?: number
  /** Text alignment: "left" | "center". */
  alignment?: "left" | "center"
  className?: string
  href?: string
}

export function TextLogoView({
  mainLine,
  secondLine = "",
  mainFont,
  secondFont,
  color,
  mainFontSize = "14px",
  secondLineScale = 0.5,
  secondFontSize: secondFontSizeProp,
  mainLetterSpacing,
  secondLetterSpacing = 35,
  lineSpacing,
  alignment = "left",
  className = "",
  href
}: TextLogoViewProps): React.ReactElement {
  useEffect(() => {
    const url = getLogoFontsStylesheetUrl()
    if (document.querySelector(`link[href="${url}"]`)) return
    const link = document.createElement("link")
    link.href = url
    link.rel = "stylesheet"
    link.crossOrigin = "anonymous"
    document.head.appendChild(link)
  }, [])

  const secondSize =
    secondFontSizeProp != null
      ? secondFontSizeProp
      : `calc(${mainFontSize} * ${secondLineScale})`
  const mainLetterSpacingEm =
    mainLetterSpacing != null ? `${mainLetterSpacing / 100}em` : undefined
  const secondLetterSpacingEm = `${(secondLetterSpacing ?? 35) / 100}em`
  const alignClass = alignment === "center" ? "items-center" : "items-start"
  const alignStyle = alignment === "center" ? { textAlign: "center" as const } : undefined

  const gapPx = lineSpacing != null && lineSpacing >= 0 ? lineSpacing : 0

  const content = (
    <div
      className={`flex flex-col justify-center leading-tight ${alignClass}`}
      style={{
        ...alignStyle,
        gap: `${gapPx}px`
      }}
    >
      <span
        style={{
          fontFamily: `'${mainFont}', sans-serif`,
          fontSize: mainFontSize,
          fontWeight: 400,
          color,
          letterSpacing: mainLetterSpacingEm
        }}
      >
        {mainLine || "Practice Name"}
      </span>
      {secondLine ? (
        <span
          className="uppercase"
          style={{
            fontFamily: `'${secondFont}', sans-serif`,
            fontSize: secondSize,
            letterSpacing: secondLetterSpacingEm,
            color,
            lineHeight: 1.2
          }}
        >
          {secondLine}
        </span>
      ) : null}
    </div>
  )

  if (href) {
    return (
      <a href={href} className={className} style={{ color }}>
        {content}
      </a>
    )
  }
  return <div className={className}>{content}</div>
}
